"use client";

import { useEffect, useState } from "react";
import styled from "styled-components";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useApp } from "@/contexts/AppContext";
import { useCart } from "@/contexts/CartContext";
import { FaShoppingCart } from "react-icons/fa";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import ProfileDropdown from "@/components/ProfileDropdown";

const Header = styled.header`
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;
  position: sticky;
  top: 0;
  z-index: 100;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }

  img {
    height: 40px;
    width: auto;
    object-fit: contain;

    @media (max-width: 768px) {
      height: 32px;
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  font-size: 1rem;
  font-weight: ${(props) => (props.$active ? "700" : "600")};
  color: ${(props) => (props.$active ? "#c8553d" : "#666")};
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.3s;

  &:hover {
    color: #c8553d;
    background: #fff5f5;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const UserName = styled.span`
  font-weight: 600;
  color: #333;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DashboardButton = styled.button`
  padding: 0.5rem 1rem;
  background: #558b8d;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
  white-space: nowrap;
  font-size: 1rem;

  &:hover {
    background: #467072;
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.7rem;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
  }
`;

const CartIconButton = styled.button`
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #558b8d;
  transition: all 0.3s;

  &:hover {
    color: #467072;
    transform: scale(1.1);
  }

  svg {
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 0.4rem;

    svg {
      font-size: 1.3rem;
    }
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  background: #c8553d;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  border: 2px solid white;

  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    font-size: 0.7rem;
  }
`;

const AuthButton = styled.button<{ $primary?: boolean }>`
  padding: 0.45rem 1rem;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  background: ${(props) => (props.$primary ? "#558b8d" : "transparent")};
  color: ${(props) => (props.$primary ? "white" : "#558b8d")};
  border: 2px solid #558b8d;

  &:hover {
    background: ${(props) => (props.$primary ? "#467072" : "#f0f8f8")};
    border-color: #467072;
    color: ${(props) => (props.$primary ? "white" : "#467072")};
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.7rem;
    font-size: 0.85rem;
  }
`;

interface AppHeaderProps {
  showNav?: boolean;
  showGreeting?: boolean;
  showOrders?: boolean;
  currentPage?:
    | "feed"
    | "meals"
    | "cart"
    | "orders"
    | "artisan-dashboard"
    | "profile";
  logoLink?: string;
}

export default function AppHeader({
  showNav = true,
  showGreeting = true,
  showOrders = true,
  currentPage,
  logoLink = "/",
}: AppHeaderProps) {
  const router = useRouter();
  const { user } = useApp();
  const { getCartItemCount } = useCart();
  const [isArtisan, setIsArtisan] = useState(false);
  const [checkingArtisan, setCheckingArtisan] = useState(true);

  const cartItemCount = getCartItemCount();

  useEffect(() => {
    const checkArtisanStatus = async () => {
      if (user?.uid) {
        try {
          const userDoc = await getDoc(doc(db, "accounts", user.uid));
          if (userDoc.exists()) {
            setIsArtisan(userDoc.data().isArtisan || false);
          }
        } catch (error) {
          console.error("Error checking artisan status:", error);
        }
      }
      setCheckingArtisan(false);
    };

    checkArtisanStatus();
  }, [user]);

  return (
    <Header>
      <HeaderContent>
        <LogoButton
          onClick={() => router.push(logoLink)}
          aria-label="Go to home"
        >
          <Image
            src="/drizl-logo.svg"
            alt="Drizl Logo"
            width={40}
            height={40}
            priority
          />
        </LogoButton>

        {showNav && (
          <Nav>
            <NavLink
              $active={currentPage === "feed"}
              onClick={() => router.push("/feed")}
            >
              Browse
            </NavLink>
            {showOrders && user && (
              <NavLink
                $active={currentPage === "orders"}
                onClick={() => router.push("/orders")}
              >
                My Orders
              </NavLink>
            )}
            {!checkingArtisan && isArtisan && (
              <NavLink
                $active={currentPage === "artisan-dashboard"}
                onClick={() => router.push("/artisan-dashboard")}
              >
                Dashboard
              </NavLink>
            )}
          </Nav>
        )}

        <UserSection>
          {showGreeting && user && (
            <UserName>Hi, {user?.displayName || user?.email}</UserName>
          )}
          {!checkingArtisan && isArtisan && !showNav && (
            <DashboardButton onClick={() => router.push("/artisan-dashboard")}>
              Artisan Dashboard
            </DashboardButton>
          )}
          {!user && (
            <>
              <AuthButton onClick={() => router.push("/login")}>
                Login
              </AuthButton>
              <AuthButton $primary onClick={() => router.push("/register")}>
                Register
              </AuthButton>
            </>
          )}
          {user && <ProfileDropdown isArtisan={isArtisan} />}
          {user && (
            <CartIconButton
              onClick={() => router.push("/cart")}
              title="View Cart"
            >
              <FaShoppingCart />
              {cartItemCount > 0 && <CartBadge>{cartItemCount}</CartBadge>}
            </CartIconButton>
          )}
        </UserSection>
      </HeaderContent>
    </Header>
  );
}
